import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  platformCreateOrgUserInput,
  type PlatformCreateOrgUserInput,
  type PlatformCreateOrgUserResult,
  type PlatformOrgRole,
  type PlatformOrgRow,
  type PlatformPage,
} from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage, HttpError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { applyServerError, zodForm } from '../catalog/lib';
import { CredentialFields, CredentialResult } from './credential-fields';
import { normalizeCredential } from './create-org-modal';

const DEFAULTS: PlatformCreateOrgUserInput = { name: '', email: '', roleKey: '', credential: 'link', password: '' };

export function useOrgRoles(orgId: string | null) {
  return useQuery({
    queryKey: ['platform', 'org', orgId, 'roles'],
    queryFn: () => api.get<PlatformOrgRole[]>(`/platform/orgs/${orgId}/roles`),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
}

/** Escolha de organização com busca no servidor (a lista pode ter centenas de itens). */
function OrgPicker({ value, onChange, error }: { value: PlatformOrgRow | null; onChange: (o: PlatformOrgRow) => void; error?: string }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);
  const q = useQuery({
    queryKey: ['platform', 'orgs', 'picker', debounced],
    queryFn: () => api.get<PlatformPage<PlatformOrgRow>>('/platform/orgs', { page: 1, pageSize: 8, sort: 'name', order: 'asc', q: debounced }),
    placeholderData: (p) => p,
  });
  return (
    <Field id="au-org-search" label="Organização" error={error}>
      <Input id="au-org-search" placeholder="Buscar por nome ou endereço" autoComplete="off" value={term} onChange={(e) => setTerm(e.target.value)} />
      <ul role="listbox" aria-label="Organizações" className="max-h-44 overflow-y-auto rounded-md border">
        {(q.data?.data ?? []).map((o) => {
          const selected = value?.id === o.id;
          return (
            <li key={o.id} role="option" aria-selected={selected}>
              <button type="button" onClick={() => onChange(o)} className={cn('flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted', selected && 'bg-accent/50')}>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{o.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{o.slug}</span>
                </span>
                {selected && <Check className="size-4 shrink-0 text-primary" aria-hidden />}
              </button>
            </li>
          );
        })}
        {q.data && q.data.data.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">Nenhuma organização encontrada.</li>}
        {q.isLoading && <li className="px-3 py-2 text-sm text-muted-foreground">Buscando…</li>}
      </ul>
    </Field>
  );
}

/**
 * Cria um usuário (ou vincula um e-mail existente) a uma organização, com papel e credencial.
 * Com `org` fixo (detalhe da organização) não mostra a busca; sem ele (tela Usuários) mostra.
 */
export function AddUserModal({ open, onOpenChange, org }: { open: boolean; onOpenChange: (v: boolean) => void; org?: { id: string; name: string } }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<PlatformOrgRow | null>(null);
  const [orgError, setOrgError] = useState<string | undefined>();
  const [result, setResult] = useState<PlatformCreateOrgUserResult | null>(null);
  const orgId = org?.id ?? picked?.id ?? null;
  const roles = useOrgRoles(orgId);
  const form = useForm<PlatformCreateOrgUserInput>({
    resolver: zodForm<PlatformCreateOrgUserInput>(platformCreateOrgUserInput, (v) => normalizeCredential(v)),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      setResult(null);
      setPicked(null);
      setOrgError(undefined);
      form.reset(DEFAULTS);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // papel padrão: o primeiro que não seja dono
  useEffect(() => {
    if (roles.data && !form.getValues('roleKey')) form.setValue('roleKey', (roles.data.find((r) => r.key !== 'owner') ?? roles.data[0])?.key ?? '');
  }, [roles.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: (v: PlatformCreateOrgUserInput) => api.post<PlatformCreateOrgUserResult>(`/platform/orgs/${orgId}/users`, normalizeCredential(v)),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success(r.created ? 'Usuário criado' : 'Usuário vinculado à organização');
      setResult(r);
    },
    onError: (e) => {
      if (e instanceof HttpError && (e.code === 'already_member' || e.code === 'email_taken' || e.code === 'platform_admin_cannot_join' || e.code === 'user_removed')) form.setError('email', { message: e.message });
      else if (e instanceof HttpError && e.status >= 500) toast.error(errorMessage(e));
      else applyServerError(form, e);
    },
  });
  const e = form.formState.errors;
  const roleKey = form.watch('roleKey');
  const targetName = org?.name ?? picked?.name;

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={result ? (result.created ? 'Usuário criado' : 'Usuário vinculado') : 'Novo usuário'}
      description={result ? undefined : targetName ? `Adiciona uma pessoa à equipe de ${targetName}.` : 'Escolha a organização e informe os dados da pessoa.'}
      footer={
        result ? (
          <Button onClick={() => onOpenChange(false)}>Concluir</Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="add-user" disabled={create.isPending}>
              {create.isPending ? 'Salvando…' : 'Adicionar usuário'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <CredentialResult credential={form.getValues('credential')} setupUrl={result.setupUrl} emailSent={result.emailSent} created={result.created} who={result.email} />
      ) : (
        <form
          id="add-user"
          onSubmit={form.handleSubmit((v) => {
            if (!orgId) {
              setOrgError('Escolha a organização');
              return;
            }
            create.mutate(v);
          })}
          className="grid gap-4"
          noValidate
        >
          {!org && (
            <OrgPicker
              value={picked}
              onChange={(o) => {
                setPicked(o);
                setOrgError(undefined);
                form.setValue('roleKey', '');
              }}
              error={orgError}
            />
          )}
          <Field id="au-name" label="Nome" error={e.name?.message}>
            <Input id="au-name" autoComplete="off" aria-invalid={!!e.name} {...form.register('name')} />
          </Field>
          <Field id="au-email" label="E-mail" hint="Se o e-mail já tem conta, ela é só vinculada à organização (a senha não muda)." error={e.email?.message}>
            <Input id="au-email" type="email" autoComplete="off" aria-invalid={!!e.email} {...form.register('email')} />
          </Field>
          <Field id="au-role" label="Papel" error={e.roleKey?.message}>
            <Select value={roleKey} onValueChange={(v) => form.setValue('roleKey', v, { shouldValidate: true })} disabled={!orgId}>
              <SelectTrigger id="au-role" className="w-full">
                <SelectValue placeholder={orgId ? 'Escolha um papel' : 'Escolha a organização primeiro'} />
              </SelectTrigger>
              <SelectContent>
                {(roles.data ?? []).map((r) => (
                  <SelectItem key={r.key} value={r.key}>
                    {r.key === 'owner' ? 'Administrador (dono)' : r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <CredentialFields form={form} idPrefix="au" errorPassword={e.password?.message} />
        </form>
      )}
    </FormModal>
  );
}
